import { Autocomplete, Box, Button, Chip, Grid, Stack, TextField, Typography } from '@mui/material'
import fileSize from 'filesize'
import { AssetFolder } from 'graphql/types'
import { Controller, useForm } from 'react-hook-form'

export interface AssetFolderPanelProps {
    folder: AssetFolder
    tagList: string[]
    onCancel: () => void
    onSubmit: (title: string, tags: string[]) => void
}

interface AssetFolderPanelInputs {
    Title: string
    Tags: string[]
}

const AssetFolderPanel = (props: AssetFolderPanelProps): JSX.Element => {
    const { folder, onCancel, onSubmit, tagList } = props
    const { handleSubmit, control } = useForm<AssetFolderPanelInputs>({
        mode: 'onSubmit',
        reValidateMode: 'onBlur',
        defaultValues: {
            Title: folder.name,
            Tags: folder.tags ?? [],
        },
    })

    const handleOnSave = () => {
        handleSubmit((data: AssetFolderPanelInputs) => {
            onSubmit(data.Title, data.Tags)
        })()
    }

    return (
        <Box display="flex" flexDirection="column" flex={1} p={2}>
            <Grid container direction={'row'} justifyContent={'space-between'} alignItems={'center'} mb={2}>
                <Typography variant="h6">{folder.name}</Typography>
            </Grid>
            <Grid container justifyContent={'space-between'}>
                <Grid item xs={12}>
                    <Typography variant="body1"> Size: {fileSize(1234)} </Typography>
                </Grid>
                <Grid item xs={12} mb={2}>
                    <Typography variant="body1">Created: {folder.createdAt} </Typography>
                </Grid>
                <Grid container mb={2}>
                    <Controller
                        name="Title"
                        control={control}
                        rules={{
                            required: 'Please enter a name for the folder',
                            maxLength: { value: 150, message: 'The order name may only be 150 characters long.' },
                        }}
                        render={({ field, fieldState }) => (
                            <TextField
                                label="Title"
                                inputProps={{ maxLength: 150 }}
                                variant={'filled'}
                                fullWidth={true}
                                {...field}
                                helperText={fieldState.error?.message ?? ''}
                            />
                        )}
                    />
                </Grid>
                <Grid container mb={2}>
                    <Controller
                        name="Tags"
                        control={control}
                        render={({ field }) => (
                            <Autocomplete
                                multiple
                                fullWidth
                                id="Tags"
                                defaultValue={folder.tags ?? []}
                                options={tagList}
                                freeSolo
                                renderTags={(value: readonly string[], getTagProps) =>
                                    value.map((option: string, index: number) => (
                                        // eslint-disable-next-line react/jsx-key
                                        <Chip variant="outlined" label={option} {...getTagProps({ index })} />
                                    ))
                                }
                                onChange={(event, value) => {
                                    field.onChange(value)
                                }}
                                renderInput={(params) => <TextField {...params} variant="filled" label="Tags" />}
                            />
                        )}
                    />
                </Grid>
            </Grid>
            <Stack direction="row" justifyContent="flex-end" alignItems="center" spacing={2}>
                <Button variant="contained" color={'secondary'} onClick={onCancel}>
                    Cancel
                </Button>
                <Button variant="contained" onClick={handleOnSave}>
                    Save
                </Button>
            </Stack>
        </Box>
    )
}

export default AssetFolderPanel
